import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { UndoRedoAvailability, UndoRedoResult } from './types';
import { invalidateSessionHistory } from './sessionChangeHistoryQuery';
import {
  mergeSessionIntoScheduleCaches,
  normalizeUndoSessionPayload,
} from '@/lib/sessionScheduleCache';
import { markSessionUpdatedByMutation } from '@/lib/sessionRealtimeDedup';

const STORAGE_KEY = 'tracker:lastUndoSessionId';

export interface SessionUndoRedoUiSnapshot {
  sessionId: string | null;
  canUndo: boolean;
  canRedo: boolean;
  undoPreview: string | null;
  redoPreview: string | null;
  isUndoing: boolean;
  isRedoing: boolean;
}

const EMPTY_AVAILABILITY: UndoRedoAvailability = {
  can_undo: false,
  can_redo: false,
  undo_preview: null,
  redo_preview: null,
};

function readStoredSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(STORAGE_KEY);
}

function writeStoredSessionId(sessionId: string | null): void {
  if (typeof window === 'undefined') return;
  if (sessionId) {
    sessionStorage.setItem(STORAGE_KEY, sessionId);
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

function freezeSnapshot(snapshot: SessionUndoRedoUiSnapshot): SessionUndoRedoUiSnapshot {
  return Object.freeze(snapshot);
}

function snapshotFrom(
  sessionId: string | null,
  availability: UndoRedoAvailability,
  pending: Pick<SessionUndoRedoUiSnapshot, 'isUndoing' | 'isRedoing'>
): SessionUndoRedoUiSnapshot {
  return freezeSnapshot({
    sessionId,
    canUndo: availability.can_undo,
    canRedo: availability.can_redo,
    undoPreview: availability.undo_preview,
    redoPreview: availability.redo_preview,
    isUndoing: pending.isUndoing,
    isRedoing: pending.isRedoing,
  });
}

function snapshotsEqual(a: SessionUndoRedoUiSnapshot, b: SessionUndoRedoUiSnapshot): boolean {
  return (
    a.sessionId === b.sessionId &&
    a.canUndo === b.canUndo &&
    a.canRedo === b.canRedo &&
    a.undoPreview === b.undoPreview &&
    a.redoPreview === b.redoPreview &&
    a.isUndoing === b.isUndoing &&
    a.isRedoing === b.isRedoing
  );
}

let availabilityState: UndoRedoAvailability = EMPTY_AVAILABILITY;
let snapshot = snapshotFrom(readStoredSessionId(), availabilityState, {
  isUndoing: false,
  isRedoing: false,
});
const listeners = new Set<() => void>();

function emitIfChanged(next: SessionUndoRedoUiSnapshot): void {
  if (snapshotsEqual(snapshot, next)) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

function updateSnapshot(
  patch: Partial<SessionUndoRedoUiSnapshot>,
  nextAvailability = availabilityState
): void {
  availabilityState = nextAvailability;
  emitIfChanged(
    freezeSnapshot({
      ...snapshot,
      ...patch,
      canUndo: nextAvailability.can_undo,
      canRedo: nextAvailability.can_redo,
      undoPreview: nextAvailability.undo_preview,
      redoPreview: nextAvailability.redo_preview,
    })
  );
}

export function getLastUndoRedoSessionId(): string | null {
  return snapshot.sessionId ?? readStoredSessionId();
}

export function setLastUndoRedoSessionId(sessionId: string | null): void {
  writeStoredSessionId(sessionId);
  if (snapshot.sessionId === sessionId) return;
  updateSnapshot({ sessionId }, EMPTY_AVAILABILITY);
}

export function subscribeSessionUndoRedoStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSessionUndoRedoSnapshot(): SessionUndoRedoUiSnapshot {
  return snapshot;
}

export async function refreshSessionUndoRedoAvailability(
  queryClient: QueryClient,
  sessionId: string | null | undefined
): Promise<void> {
  void queryClient;
  if (!sessionId) {
    setLastUndoRedoSessionId(null);
    return;
  }
  const availability = await api.sessions.getChangesAvailability(sessionId);
  if (snapshot.sessionId !== sessionId) {
    writeStoredSessionId(sessionId);
  }
  updateSnapshot({ sessionId }, availability);
}

function applySessionPayload(queryClient: QueryClient, payload: unknown): string | null {
  const session = normalizeUndoSessionPayload(payload);
  if (!session) return null;
  markSessionUpdatedByMutation(session.id);
  mergeSessionIntoScheduleCaches(queryClient, session);
  invalidateSessionHistory(queryClient, session.id);
  return session.id;
}

function setPending(kind: 'undo' | 'redo', value: boolean): void {
  updateSnapshot(kind === 'undo' ? { isUndoing: value } : { isRedoing: value });
}

export async function undoLastSessionChange(
  queryClient: QueryClient,
  sessionId?: string | null
): Promise<UndoRedoResult> {
  const targetSessionId = sessionId ?? getLastUndoRedoSessionId();
  setPending('undo', true);
  try {
    const result = targetSessionId
      ? await api.sessions.undo(targetSessionId)
      : await api.sessions.undoGlobal();
    if (result.ok) {
      const payloadSessionId = applySessionPayload(queryClient, result.session);
      const resultSessionId = result.session_id ?? payloadSessionId ?? targetSessionId;
      if (resultSessionId) {
        setLastUndoRedoSessionId(resultSessionId);
        await refreshSessionUndoRedoAvailability(queryClient, resultSessionId);
      }
    }
    return result;
  } finally {
    setPending('undo', false);
  }
}

export async function redoLastSessionChange(
  queryClient: QueryClient,
  sessionId: string | null | undefined
): Promise<UndoRedoResult> {
  if (!sessionId) {
    return { ok: false, error_code: 'nothing_to_redo', message: 'Нет изменений для повтора' };
  }
  setPending('redo', true);
  try {
    const result = await api.sessions.redo(sessionId);
    if (result.ok) {
      const payloadSessionId = applySessionPayload(queryClient, result.session);
      const resultSessionId = result.session_id ?? payloadSessionId ?? sessionId;
      setLastUndoRedoSessionId(resultSessionId);
      await refreshSessionUndoRedoAvailability(queryClient, resultSessionId);
    }
    return result;
  } finally {
    setPending('redo', false);
  }
}
